/**
 * Muze quiz lead form (sections/muze-quiz.liquid).
 *
 * A light three-tap quiz (budget, model, fabric) followed by a three-field
 * contact form. Modelled on the Request-a-Quote flow (order-by-phone.js):
 * the submit is a fetch() to the Mira app proxy, which emails the lead to
 * the sales team and fires the conversion server-side (Meta CAPI + GA4).
 * The visitor never leaves the page; the result renders in place, so
 * exactly one conversion is recorded, and only after the lead is confirmed
 * sent.
 *
 * Models are server-rendered from the section's collection. Fabrics are
 * derived client-side from the chosen product's variants, whose titles
 * encode the fabric ("TOBAGO 24 (210x90x80cm)") - the same strip-parens
 * convention the free-ottoman offer used. Swatch imagery resolves in a
 * chain: fabric catalogue image (optional endpoint), else the variant's
 * image, else the product image. A missing image never blocks a tile.
 */
import { generateEventId, collectAttribution, pushLeadDataLayer } from '@theme/lead-tracking';

const STEP_ORDER = ['budget', 'model', 'fabric', 'contact'];

/** "EXCITE (SG) BALTIC (210x90x80cm)" -> "EXCITE (SG) BALTIC" */
function fabricNameFromVariantTitle(title) {
  return String(title || '')
    .replace(/\s*\([^()]*\)\s*$/, '')
    .trim();
}

/** Loose key for matching theme fabric names against catalogue names. */
function fabricKey(name) {
  return String(name || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
}

function formatRand(cents) {
  const rands = Math.round(Number(cents || 0) / 100);
  return `R ${rands.toLocaleString('en-US')}`;
}

function pushQuizStep(stepName, stepIndex) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'quiz_step', step_name: stepName, step_index: stepIndex });
  } catch {
    /* tracking must never surface to the customer */
  }
}

class MuzeQuizComponent extends HTMLElement {
  #state = { budget: '', product: null, fabric: null };

  /** Visited steps, for the back button. */
  #history = [];

  #current = 'budget';

  /** fabricKey(name) -> swatch image URL, filled lazily from the catalogue. */
  #swatches = new Map();

  /** Range prefixes already requested from the catalogue, to avoid refetching
   *  when the visitor goes back and picks a different model. */
  #swatchPrefixes = new Set();

  #submitting = false;

  connectedCallback() {
    if (this.dataset.quizInit === '1') return;
    this.dataset.quizInit = '1';

    this.addEventListener('click', this.#handleClick);
    this.#ref('backButton')?.addEventListener('click', this.#goBack);
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);

    this.#show('budget');
  }

  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  #step(name) {
    return this.querySelector(`[data-quiz-step="${name}"]`);
  }

  // ── Step navigation ─────────────────────────────────────────────

  #show(name, { record = true } = {}) {
    if (record && this.#current !== name) this.#history.push(this.#current);
    this.#current = name;

    for (const section of this.querySelectorAll('[data-quiz-step]')) {
      section.hidden = section.getAttribute('data-quiz-step') !== name;
    }

    const back = this.#ref('backButton');
    const count = this.#ref('stepCount');
    const bar = this.#ref('progressBar');
    const index = STEP_ORDER.indexOf(name);

    if (name === 'result') {
      if (back) back.hidden = true;
      if (count) count.textContent = '';
      if (bar) bar.style.width = '100%';
      return;
    }

    if (back) back.hidden = this.#history.length === 0;
    if (count) count.textContent = `Step ${index + 1} of ${STEP_ORDER.length}`;
    if (bar) bar.style.width = `${((index + 1) / (STEP_ORDER.length + 1)) * 100}%`;
    pushQuizStep(name, index + 1);
  }

  #goBack = () => {
    const previous = this.#history.pop();
    if (previous) this.#show(previous, { record: false });
  };

  #handleClick = (event) => {
    const budget = event.target.closest('[data-quiz-budget]');
    if (budget && this.contains(budget)) {
      this.#state.budget = budget.getAttribute('data-quiz-budget') || '';
      this.#show('model');
      return;
    }

    const product = event.target.closest('[data-quiz-product]');
    if (product && this.contains(product)) {
      this.#selectProduct(product.dataset);
      return;
    }

    if (event.target.closest('[data-quiz-skip-model]')) {
      this.#state.product = null;
      this.#state.fabric = null;
      this.#updateSummary();
      this.#show('contact');
      return;
    }

    const fabric = event.target.closest('[data-quiz-fabric]');
    if (fabric && this.contains(fabric)) {
      const index = Number(fabric.getAttribute('data-quiz-fabric'));
      this.#state.fabric = this.#state.product?.fabrics?.[index] ?? null;
      this.#updateSummary();
      this.#show('contact');
      return;
    }

    if (event.target.closest('[data-quiz-skip-fabric]')) {
      this.#state.fabric = null;
      this.#updateSummary();
      this.#show('contact');
    }
  };

  // ── Model + fabric data ─────────────────────────────────────────

  #selectProduct(dataset) {
    this.#state.product = {
      handle: dataset.handle,
      title: dataset.title,
      url: dataset.url,
      id: dataset.id,
      type: dataset.type,
      priceMin: Number(dataset.priceMin || 0),
      image: dataset.image || '',
      fabrics: null,
    };
    this.#state.fabric = null;
    this.#show('fabric');
    void this.#loadFabrics();
  }

  async #loadFabrics() {
    const product = this.#state.product;
    if (!product) return;

    const grid = this.#ref('fabricGrid');
    const loading = this.#ref('fabricLoading');
    if (grid) grid.replaceChildren();
    if (loading) loading.hidden = false;

    let fabrics = [];
    try {
      const res = await fetch(`/products/${product.handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      fabrics =
        this.dataset.quizMode === 'options'
          ? this.#choicesFromOptions(data)
          : this.#choicesFromVariantTitles(data);
    } catch {
      fabrics = [];
    }

    product.fabrics = fabrics;
    if (loading) loading.hidden = true;

    // The quiz must never dead-end on a data problem - with no fabrics to
    // offer, move straight on to contact details.
    if (!fabrics.length) {
      this.#updateSummary();
      this.#show('contact', { record: false });
      return;
    }

    if (grid) {
      fabrics.forEach((fabric, index) => {
        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'mqz__fabric';
        tile.setAttribute('data-quiz-fabric', String(index));

        const img = document.createElement('img');
        img.className = 'mqz__fabric-swatch';
        img.alt = '';
        img.loading = 'lazy';
        img.setAttribute('data-quiz-fabric-key', fabricKey(fabric.name));
        const fallbackSrc =
          this.#swatches.get(fabricKey(fabric.name)) || fabric.image || this.#state.product.image;
        if (fallbackSrc) img.src = fallbackSrc;

        const name = document.createElement('span');
        name.className = 'mqz__fabric-name';
        name.textContent = fabric.name;

        const price = document.createElement('span');
        price.className = 'mqz__fabric-price';
        price.textContent = formatRand(fabric.price);

        tile.append(img, name, price);
        grid.append(tile);
      });
    }

    if (this.dataset.quizMode !== 'options') {
      void this.#upgradeSwatches(fabrics, grid);
    }
  }

  /**
   * Furniture: variant titles encode the fabric ("TOBAGO 24 (210x90x80cm)").
   * One entry per fabric name; keep the cheapest variant so the quoted price
   * is a true "from" price when a fabric spans several dimensions.
   */
  #choicesFromVariantTitles(data) {
    const byName = new Map();
    for (const variant of data.variants || []) {
      if (variant.available === false) continue; // sold out — never recommend it
      const name = fabricNameFromVariantTitle(variant.title);
      if (!name) continue;
      const existing = byName.get(name);
      if (!existing || variant.price < existing.price) {
        byName.set(name, {
          name,
          price: variant.price,
          variantId: variant.id,
          variantTitle: variant.title,
          image: variant.featured_image?.src || '',
        });
      }
    }
    return [...byName.values()];
  }

  /**
   * Bedding: products carry real Size and Colour options. The first-step
   * tile answer doubles as the size, so when it matches a size value the
   * colour tiles show that exact variant (image and price); otherwise each
   * colour shows its cheapest variant as a "from" price.
   */
  #choicesFromOptions(data) {
    const options = data.options || [];
    const sizeIndex = options.findIndex((o) => /size/i.test(o.name));
    const colourIndex = options.findIndex((o) => /colou?r/i.test(o.name));
    if (colourIndex === -1) return this.#choicesFromVariantTitles(data);

    const tileAnswer = (this.#state.budget || '').trim().toLowerCase();
    const sizeValues = sizeIndex === -1 ? [] : options[sizeIndex].values || [];
    const chosenSize =
      sizeValues.find((v) => String(v).trim().toLowerCase() === tileAnswer) || null;

    const byColour = new Map();
    for (const variant of data.variants || []) {
      if (variant.available === false) continue; // sold out — never recommend it
      const colour = variant.options?.[colourIndex];
      if (!colour) continue;
      if (chosenSize && variant.options?.[sizeIndex] !== chosenSize) continue;
      const existing = byColour.get(colour);
      if (!existing || variant.price < existing.price) {
        byColour.set(colour, {
          name: colour,
          price: variant.price,
          variantId: variant.id,
          variantTitle: variant.title,
          image: variant.featured_image?.src || '',
        });
      }
    }
    return [...byColour.values()];
  }

  /**
   * Optional swatch upgrade: the Mira fabric catalogue (11k+ fabrics, paged)
   * serves true swatch photos. The full list is too big to fetch, so query
   * it by range prefix ("TOBAGO", "EXCITE (SG)") - one small request covers
   * every colourway of a range. Tiles are already rendered with product or
   * variant imagery; matches swap in as they arrive. Purely cosmetic: any
   * failure leaves the fallbacks in place.
   */
  async #upgradeSwatches(fabrics, grid) {
    const endpoint = (this.dataset.fabricsEndpoint || '').trim();
    if (!endpoint || !fabrics.length) return;
    const separator = endpoint.includes('?') ? '&' : '?';

    const prefixes = new Set();
    for (const fabric of fabrics) {
      const words = fabric.name.split(/\s+/);
      // Short first words ("LA PAZ") need a second word to be a useful search.
      const prefix = words[0].length >= 4 ? words[0] : words.slice(0, 2).join(' ');
      if (prefix && !this.#swatchPrefixes.has(prefix)) prefixes.add(prefix);
    }

    await Promise.allSettled(
      [...prefixes].map(async (prefix) => {
        this.#swatchPrefixes.add(prefix);
        const res = await fetch(
          `${endpoint}${separator}pageSize=60&search=${encodeURIComponent(prefix)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.fabrics;
        for (const entry of list || []) {
          const url = entry?.img || entry?.colorUrl || entry?.swatchUrl;
          if (entry?.name && url) this.#swatches.set(fabricKey(entry.name), url);
        }
      })
    );

    if (!grid || !this.#swatches.size) return;
    for (const img of grid.querySelectorAll('[data-quiz-fabric-key]')) {
      const swatch = this.#swatches.get(img.getAttribute('data-quiz-fabric-key'));
      if (swatch) img.src = swatch;
    }
  }

  // ── Contact + submit ────────────────────────────────────────────

  #updateSummary() {
    const card = this.#ref('summaryCard');
    if (!card) return;
    const { product, fabric } = this.#state;

    if (!product) {
      card.hidden = true;
      return;
    }

    card.hidden = false;
    const image = this.#ref('summaryImage');
    const chosenImage = fabric?.image || product.image || '';
    if (image) {
      image.hidden = !chosenImage;
      if (chosenImage) image.src = chosenImage;
    }
    const title = this.#ref('summaryTitle');
    if (title) title.textContent = product.title;
    const meta = this.#ref('summaryMeta');
    if (meta) meta.textContent = fabric ? fabric.name : 'Fabric to be confirmed';
    const price = this.#ref('summaryPrice');
    if (price) price.textContent = formatRand(fabric ? fabric.price : product.priceMin);
  }

  #buildSummaryText() {
    const { budget, product, fabric } = this.#state;
    const lines = ['Quiz answers:'];
    lines.push(`  ${this.dataset.tileLabel || 'Budget'}: ${budget || 'Not sure yet'}`);
    lines.push(`  ${this.dataset.modelLabel || 'Model'}: ${product ? product.title : 'Not sure yet'}`);
    lines.push(`  ${this.dataset.choiceLabel || 'Fabric'}: ${fabric ? fabric.name : 'Not sure yet'}`);
    if (product) {
      lines.push(`  Price shown: ${formatRand(fabric ? fabric.price : product.priceMin)}`);
    }
    return lines.join('\n');
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;

    const form = event.target;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const firstName = this.#ref('firstName')?.value?.trim() || '';
    const email = this.#ref('emailInput')?.value?.trim() || '';
    const phone = this.#ref('phoneInput')?.value?.trim() || '';
    const honeypot = this.#ref('honeypot')?.value?.trim() || '';

    const { product, fabric } = this.#state;
    const eventId = generateEventId();
    const value = product ? (fabric ? fabric.price : product.priceMin) / 100 : 0;
    const priceDisplay = product ? formatRand(fabric ? fabric.price : product.priceMin) : '';
    const productUrl = product?.url?.startsWith('/')
      ? window.location.origin + product.url
      : product?.url || '';

    const requestBody = {
      company: honeypot,
      lead_source: this.dataset.leadSource || 'quiz',
      first_name: firstName,
      last_name: '',
      email,
      phone,
      country: 'za',
      message: '',
      summary: this.#buildSummaryText(),
      product: product
        ? {
            title: product.title,
            handle: product.handle,
            url: productUrl,
            variant_title: fabric?.variantTitle || '',
            price: value,
            currency: 'ZAR',
          }
        : null,
      image_url: fabric?.image || product?.image || '',
      material_name: fabric?.name || '',
      material_swatch_url: (fabric && this.#swatches.get(fabricKey(fabric.name))) || '',
      price_display: priceDisplay,
      page_url: window.location.href,
      event_id: eventId,
      attribution: collectAttribution(),
    };

    const submitButton = this.#ref('submitButton');
    const originalLabel = submitButton?.textContent || '';
    this.#submitting = true;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending…';
    }
    this.#clearStatus();

    try {
      const res = await fetch(this.dataset.leadEndpoint || '/apps/mira/lead/request-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestBody),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON response */
      }
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      // Pushed only once the lead is confirmed sent, so a failed submission
      // is never reported as a conversion.
      pushLeadDataLayer({
        eventId,
        leadSource: this.dataset.leadSource || 'quiz',
        user: { first_name: firstName, last_name: '', email, phone },
        product: product ? { title: product.title } : null,
        value,
        currency: 'ZAR',
        phase: 'success_render',
      });

      this.#renderResult();
    } catch (err) {
      console.warn('[muze-quiz] lead submit failed', err);
      this.#showStatus(
        'Sorry, something went wrong sending your details. Please try again, or email us at hello@muze.co.za.'
      );
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalLabel;
      }
    } finally {
      this.#submitting = false;
    }
  };

  #renderResult() {
    const { product, fabric } = this.#state;

    const heading = this.#ref('resultHeading');
    if (heading) heading.textContent = this.dataset.successHeading || 'Here is your match';
    const text = this.#ref('resultText');
    if (text) text.textContent = this.dataset.successText || '';

    const card = this.#ref('resultCard');
    if (card) {
      card.hidden = !product;
      if (product) {
        const image = this.#ref('resultImage');
        const chosenImage = fabric?.image || product.image || '';
        if (image) {
          image.hidden = !chosenImage;
          if (chosenImage) image.src = chosenImage;
        }
        const title = this.#ref('resultTitle');
        if (title) title.textContent = product.title;
        const meta = this.#ref('resultMeta');
        if (meta) meta.textContent = fabric ? fabric.name : 'Fabric to be confirmed';
        const price = this.#ref('resultPrice');
        if (price) price.textContent = formatRand(fabric ? fabric.price : product.priceMin);
      }
    }

    const link = this.#ref('resultProductLink');
    if (link) {
      link.hidden = !product;
      if (product) {
        link.href = product.url;
        link.textContent = `View ${product.title}`;
      }
    }

    this.#show('result');
  }

  #showStatus(message) {
    const status = this.#ref('formStatus');
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
  }

  #clearStatus() {
    const status = this.#ref('formStatus');
    if (!status) return;
    status.textContent = '';
    status.hidden = true;
  }
}

if (!customElements.get('muze-quiz-component')) {
  customElements.define('muze-quiz-component', MuzeQuizComponent);
}
